"use client";

import * as React from "react";
import {
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartLegend,
  ChartLegendContent,
  cn,
} from "visuais";

// Tipos específicos para donut
export type DonutDataItem = {
  name: string;
  value: number;
  color?: string;
  [key: string]: any;
};

export interface DonutChartProps {
  data: DonutDataItem[];
  className?: string;
  height?: number | string;
  width?: number | string;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
  loading?: boolean;
  error?: string | null;

  // Variações
  variant?: "default" | "active" | "text" | "stacked" | "interactive";

  // Para donut com texto
  centerText?: string;
  centerSubtext?: string;

  // Para donut ativo
  activeIndex?: number;
  activeColor?: string;

  // Para donut stacked
  stacked?: boolean;
  stackGap?: number;

  // Para donut interativo
  interactive?: boolean;
  onSegmentClick?: (data: DonutDataItem, index: number) => void;

  // Estilo dos segmentos
  cornerRadius?: number;
  paddingAngle?: number;

  // Tooltip customizado
  customTooltip?: React.ComponentType<any>;
}

export function DonutChart({
  data,
  className,
  height = 250,
  width = "100%",
  innerRadius = 60,
  outerRadius = 100,
  startAngle = -90,
  endAngle = 270,
  showTooltip = true,
  showLegend = false,
  loading = false,
  error = null,
  variant = "default",
  centerText,
  centerSubtext,
  activeIndex = -1,
  activeColor = "hsl(var(--primary))",
  stacked = false,
  stackGap = 10,
  interactive = false,
  onSegmentClick,
  cornerRadius = 8,
  paddingAngle = 2,
  customTooltip,
}: DonutChartProps) {
  const [hoveredIndex, setHoveredIndex] = React.useState(-1);

  // Configuração do chart config para compatibilidade com shadcn
  const chartConfig = React.useMemo(() => {
    const config: Record<string, any> = {};
    data.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: item.color || `hsl(var(--chart-${(index % 5) + 1}))`,
      };
    });
    return config;
  }, [data]);

  if (loading) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-muted-foreground text-sm">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-destructive text-sm text-center">
          Erro: {error}
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Configuração para gráficos stacked
  const getStackedRadius = (index: number) => {
    if (!stacked) return { innerRadius, outerRadius };

    const baseInner = innerRadius;
    const baseOuter = outerRadius;
    const gap = stackGap;

    return {
      innerRadius: baseInner + index * gap,
      outerRadius: baseOuter + index * gap,
    };
  };

  return (
    <div className={cn("w-full", className)}>
      <ChartContainer
        config={chartConfig}
        className="w-full"
        style={{ height, width }}
      >
        <div className="w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              {showTooltip && (
                <ChartTooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;

                    // Se há um tooltip customizado, use-o
                    if (customTooltip) {
                      const CustomTooltipComponent = customTooltip;
                      return (
                        <CustomTooltipComponent
                          active={active}
                          payload={payload}
                        />
                      );
                    }

                    // Tooltip padrão
                    const data = payload[0].payload;
                    const percentage = ((data.value / total) * 100).toFixed(1);

                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="grid gap-2">
                          <div className="flex items-center gap-2">
                            <div
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: data.color }}
                            />
                            <span className="font-medium">{data.name}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {data.value.toLocaleString()} ({percentage}%)
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              )}

              {stacked ? (
                // Donut stacked - múltiplas camadas
                data.map((item, index) => {
                  const { innerRadius: inner, outerRadius: outer } =
                    getStackedRadius(index);
                  const color =
                    item.color || `hsl(var(--chart-${(index % 5) + 1}))`;

                  return (
                    <Pie
                      key={`${item.name}-${index}`}
                      data={[item]}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={inner}
                      outerRadius={outer}
                      startAngle={startAngle}
                      endAngle={endAngle}
                      cornerRadius={cornerRadius}
                      paddingAngle={paddingAngle}
                    >
                      <Cell
                        fill={color}
                        stroke="none"
                        className={cn(
                          interactive &&
                            "cursor-pointer transition-all duration-200",
                          hoveredIndex === index && interactive && "opacity-80"
                        )}
                        onMouseEnter={() =>
                          interactive && setHoveredIndex(index)
                        }
                        onMouseLeave={() => interactive && setHoveredIndex(-1)}
                        onClick={() =>
                          interactive && onSegmentClick?.(item, index)
                        }
                      />
                    </Pie>
                  );
                })
              ) : (
                // Donut normal - uma camada
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  startAngle={startAngle}
                  endAngle={endAngle}
                  cornerRadius={cornerRadius}
                  paddingAngle={paddingAngle}
                >
                  {data.map((item, index) => {
                    let color =
                      item.color || `hsl(var(--chart-${(index % 5) + 1}))`;
                    const isActive =
                      activeIndex === index || hoveredIndex === index;
                    const isInteractive =
                      interactive || variant === "interactive";

                    // Garantir que a cor seja uma string válida
                    if (!color || color === "undefined" || color === "null") {
                      color = "#3b82f6"; // Azul padrão
                    }

                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          isActive && variant === "active" ? activeColor : color
                        }
                        stroke="none"
                        className={cn(
                          isInteractive &&
                            "cursor-pointer transition-all duration-200",
                          isActive && isInteractive && "opacity-80"
                        )}
                        onMouseEnter={() =>
                          isInteractive && setHoveredIndex(index)
                        }
                        onMouseLeave={() =>
                          isInteractive && setHoveredIndex(-1)
                        }
                        onClick={() =>
                          isInteractive && onSegmentClick?.(item, index)
                        }
                      />
                    );
                  })}
                </Pie>
              )}

              {/* Texto central para donut com texto */}
              {variant === "text" && (centerText || centerSubtext) && (
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-foreground"
                >
                  {centerText && (
                    <tspan
                      x="50%"
                      dy="-0.5em"
                      className="text-lg font-semibold"
                    >
                      {centerText}
                    </tspan>
                  )}
                  {centerSubtext && (
                    <tspan
                      x="50%"
                      dy="1.2em"
                      className="text-sm text-muted-foreground"
                    >
                      {centerSubtext}
                    </tspan>
                  )}
                </text>
              )}
            </RechartsPieChart>
          </ResponsiveContainer>

          {showLegend && (
            <div className="mt-4">
              <ChartLegend content={<ChartLegendContent />} />
            </div>
          )}
        </div>
      </ChartContainer>
    </div>
  );
}
